import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines, ParallaxImage } from "@/components/motion-primitives";

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title ? `${title} | The Bermuda Co-Host` : "The Bermuda Co-Host — Bermuda Vacation Rental Management";
    const meta = document.querySelector('meta[name="description"]');
    if (meta && description) meta.setAttribute("content", description);
  }, [title, description]);
}

export const PageHero = ({ overline, lines, copy, image, children, tall = false }) => (
  <section className={`relative flex items-end overflow-hidden bg-navy text-ivory ${tall ? "min-h-screen" : "min-h-[68vh]"}`}>
    {image && (
      <div className="absolute inset-0">
        <ParallaxImage src={image} alt="" className="h-full" speed={0.08} />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/50 to-navy/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/30" />
      </div>
    )}
    <div className="noise-overlay relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-16 pt-40 lg:px-10 lg:pb-24">
      {overline && (
        <Reveal>
          <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">{overline}</p>
        </Reveal>
      )}
      <MaskedLines
        lines={lines}
        className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.06] tracking-tight"
      />
      {copy && (
        <Reveal delay={0.5}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-ivory/80">{copy}</p>
        </Reveal>
      )}
      {children && <Reveal delay={0.65}><div className="mt-9 flex flex-wrap gap-4">{children}</div></Reveal>}
    </div>
  </section>
);

export const CtaButton = ({ to, children, variant = "solid", dark = false, testid }) => {
  const base = "inline-flex items-center gap-2.5 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300";
  const styles = {
    solid: "bg-navy text-ivory hover:bg-bermuda-dark hover:text-white",
    accent: "bg-bermuda text-navy hover:bg-ivory hover:text-navy",
    outline: dark
      ? "border border-ivory/40 text-ivory hover:border-ivory hover:bg-ivory hover:text-navy"
      : "border border-navy/30 text-navy hover:border-navy hover:bg-navy hover:text-ivory",
  };
  return (
    <Link to={to} data-testid={testid} className={`${base} ${styles[variant]} group`}>
      {children}
      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
};

export const CtaBand = ({ title = "Let's take care of your property.", copy }) => (
  <section className="bg-navy text-ivory">
    <div className="noise-overlay mx-auto max-w-[1440px] px-6 py-20 lg:px-10 lg:py-28">
      <Reveal>
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">Begin Here</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-5 max-w-3xl font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight">{title}</h2>
      </Reveal>
      {copy && <Reveal delay={0.2}><p className="mt-5 max-w-xl text-base text-ivory/70">{copy}</p></Reveal>}
      <Reveal delay={0.3}>
        <div className="mt-9 flex flex-wrap gap-4">
          <CtaButton to="/assessment" variant="accent" testid="cta-band-assessment-btn">Get a Property Assessment</CtaButton>
          <CtaButton to="/consultation" variant="outline" dark testid="cta-band-consultation-btn">Book a Consultation</CtaButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const SectionHead = ({ overline, title, copy, dark = false, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <Reveal>
      <p className={`text-[11px] font-semibold uppercase tracking-[0.3em] ${dark ? "text-bermuda" : "text-bermuda-dark"}`}>{overline}</p>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className={`mt-4 font-serif text-3xl sm:text-4xl lg:text-[44px] font-light leading-[1.12] tracking-tight ${dark ? "text-ivory" : "text-navy"}`}>
        {title}
      </h2>
    </Reveal>
    {copy && (
      <Reveal delay={0.16}>
        <p className={`mt-5 text-base leading-relaxed ${dark ? "text-ivory/70" : "text-navy/65"}`}>{copy}</p>
      </Reveal>
    )}
  </div>
);

const inputCls = "w-full border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 outline-none transition-colors focus:border-bermuda-dark";

export const Field = ({ label, children, hint, required }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70">
      {label} {required && <span className="text-bermuda-dark">*</span>}
    </span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-navy/45">{hint}</span>}
  </label>
);

export const Input = ({ className = "", ...props }) => <input {...props} className={`${inputCls} ${className}`} />;
export const Textarea = ({ className = "", ...props }) => <textarea rows={4} {...props} className={`${inputCls} ${className}`} />;
export const Select = ({ children, className = "", ...props }) => (
  <select {...props} className={`${inputCls} ${className} appearance-none`}>{children}</select>
);

export const SubmitButton = ({ loading, children, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className="inline-flex items-center gap-2.5 bg-navy px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-bermuda-dark disabled:opacity-60"
  >
    {loading ? "Sending…" : children}
    {!loading && <ArrowRight size={14} />}
  </button>
);

export const FormShell = ({ onSubmit, children, className = "" }) => (
  <form onSubmit={onSubmit} className={`grid gap-6 ${className}`}>{children}</form>
);

export function useLeadSubmit(type, onSuccess) {
  const [loading, setLoading] = useState(false);
  const submit = async (data, toast) => {
    setLoading(true);
    try {
      const { submitLead } = await import("@/lib/api");
      await submitLead(type, data);
      toast.success("Received — we'll be in touch shortly.");
      onSuccess && onSuccess();
      return true;
    } catch (e) {
      toast.error("Something went wrong. Please try again or call us directly.");
      return false;
    } finally {
      setLoading(false);
    }
  };
  return { submit, loading };
}
